// src/pages/Inbox.js
/**
 * Unified Inbox - Messages & Notifications
 * two-panel layout with mobile-first responsive design
 */
import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCheck,
  RefreshCw,
} from 'lucide-react';
import useAuth from '../hooks/useAuth';
import useNotifications from '../hooks/useNotifications';
import NotificationList from '../components/NotificationList';
import ConversationsSidebar from '../components/chat/ConversationsSidebar';
import InboxHeader from '../components/chat/InboxHeader';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { markAllNotificationsRead } from '../services/notificationsService';
import { listenChatsForUser } from '../services/chatService';
import { countUnreadChatsForUser } from '../utils/unread';
import { errorDev } from '../utils/logger.js';

const TABS = {
  MESSAGES: 'messages',
  NOTIFICATIONS: 'notifications',
};

export default function Inbox() {
  const { t, ready: i18nReady } = useTranslation(['inbox', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const safeT = useCallback((key, fallback = key) => {
    if (!i18nReady) return fallback;
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback;
    }
  }, [i18nReady, t]);

  const initialTab = searchParams.get('tab') || TABS.MESSAGES;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const {
    notifications,
    loading: notificationsLoading,
    error: notificationsError,
    unreadCount,
    refresh: refreshNotifications,
  } = useNotifications();

  React.useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Real-time unread messages count
  React.useEffect(() => {
    if (!user?.uid) {
      setUnreadMessages(0);
      return;
    }

    const unsub = listenChatsForUser(user.uid, (rows) => {
      setUnreadMessages(countUnreadChatsForUser(rows, user.uid));
    });

    return () => { try { unsub && unsub(); } catch (err) { errorDev("Operation failed:", err);} };
  }, [user?.uid]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  }, [setSearchParams]);

  const handleConversationSelect = useCallback((chatId) => {
    navigate(`/messages/${encodeURIComponent(chatId)}`);
  }, [navigate]);

  const handleMarkAllRead = useCallback(async () => {
    if (!user || isMarkingAll || unreadCount === 0) return;
    setIsMarkingAll(true);
    try {
      await markAllNotificationsRead(user.uid);
      refreshNotifications?.();
    } catch (err) {
      // silent
    errorDev("Operation failed:", err);
    } finally {
      setIsMarkingAll(false);
    }
  }, [user, isMarkingAll, unreadCount, refreshNotifications]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 mobile-nav-safe-padding">
      {/* Tabbed Header */}
      <InboxHeader
        activeTab={activeTab}
        onTabChange={handleTabChange}
        unreadCount={unreadCount}
        unreadMessages={unreadMessages}
        safeT={safeT}
      />

      <div className="max-w-5xl mx-auto">
        {/* Messages Tab */}
        {activeTab === TABS.MESSAGES && (
          <div className="h-[calc(100dvh-130px)] sm:h-[calc(100dvh-140px)]">
            <ConversationsSidebar
              onSelectConversation={handleConversationSelect}
              selectedChatId={null}
              fullWidth
            />
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === TABS.NOTIFICATIONS && (
          <div className="min-h-[calc(100dvh-130px)]">
            {!isOnline && (
              <div className="mx-4 mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                It's Orrith, not you. You are offline, so notification updates may be delayed.
              </div>
            )}

            {notificationsError && (
              <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
                <div>It's Orrith, not you. We could not refresh notifications right now.</div>
                <button
                  onClick={() => refreshNotifications?.()}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:underline dark:text-red-200"
                >
                  <RefreshCw className="w-3 h-3" />
                  Retry
                </button>
              </div>
            )}

            {unreadCount > 0 && (
              <div className="flex justify-end px-4 py-2">
                <button
                  onClick={handleMarkAllRead}
                  disabled={isMarkingAll}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isMarkingAll ? (
                    <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
                  ) : (
                    <CheckCheck className="w-4 h-4" />
                  )}
                  {safeT('inbox:markAllRead', 'Mark all read')}
                </button>
              </div>
            )}

            <div className="bg-white dark:bg-neutral-900 sm:rounded-xl sm:shadow-sm sm:border sm:border-gray-200 dark:sm:border-neutral-800 sm:mx-4 sm:mb-4 overflow-hidden">
              {notificationsLoading ? (
                <div className="py-16">
                  <LoadingSpinner text={safeT('inbox:loading', 'Loading...')} />
                </div>
              ) : (
                <NotificationList
                  notifications={notifications}
                  onClose={() => {}}
                  isCompact={false}
                  showHeader={false}
                />
              )}
            </div>

            {!notificationsLoading && notifications.length > 0 && (
              <div className="py-3 text-center text-xs text-muted-foreground">
                {notifications.length} {safeT('inbox:notifications', 'notifications')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
